
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import "./uploadRoster.css";
import { useDispatch, useSelector } from "react-redux";
import { bulkCreateOfficer } from "../store/slices/user";
import { bulkCreateSubDivision } from "../store/slices/subdivision";
import { bulkCreateCircle } from "../store/slices/circle";
import { bulkCreateStation } from "../store/slices/station";
import { bulkCreateBooth } from "../store/slices/booth";


const DEFAULT_TABS = [
  "OFFICERS",
  "SUBDIVISIONS",
  "CIRCLES",
  "STATIONS",
  "BOOTHS",
  "SPECIAL_DUTIES",
  "MASTER",
];

const TAB_CONFIG = {
  OFFICERS: {
    label: "Officers",
    icon: "👮",
    title: "Officer Roster Import",
    description: "Upload officer details and duty assignments.",
    filename: "Officer_Roster_Template.xlsx",
    template: [
      "Username,Name,Phone,Password,Badge_No,Designation",
      "officer_001,Officer Name,9876543210,password123,BADGE001,POLICE_CONSTABLE",
    ].join("\n"),
  },
  SUBDIVISIONS: {
    label: "Sub-Divisions",
    icon: "🏢",
    title: "Sub-Division Import",
    description: "Upload Sub-Division details and DSP assignments.",
    filename: "Sub_Divisions_Template.xlsx",
    template: [
      "SubDivision_ID,Name, DSP_Badge_Number, Phone",
      "SD001,Central Sub Division,BDG001,9876543210",
    ].join("\n"),
  },
  CIRCLES: {
    label: "Circles",
    icon: "📍",
    title: "Circle Import",
    description: "Upload Circle details and Inspector assignments.",
    filename: "Circles_Template.xlsx",
    template: [
      "Circle_ID,Name,SubDivision_ID,Inspector_Badge_Number,Phone",
      "CIR001,Central Circle,SD001,INS001,9876543210",
    ].join("\n"),
  },
  STATIONS: {
    label: "Police Stations",
    icon: "🚔",
    title: "Police Station Import",
    description: "Upload police station details and SHO assignments.",
    filename: "Police_Stations_Template.xlsx",
    template: [
      "Station_ID,Code,Name,SubDivision_ID,Circle_ID,SHO_Badge_Number,Contact_Number",
      "PS001,PS-CEN,Central Police Station,SD001,CIR001,BDG001,9876543210",
    ].join("\n"),
  },
  BOOTHS: {
    label: "Polling Booths",
    icon: "🏛️",
    title: "Polling Booth Import",
    description: "Upload booth details, coordinates and multiple officer assignments.",
    filename: "Polling_Booths_Template.xlsx",
    template: [
      "Booth_ID,Code,Name,Latitude,Longitude,PoliceStation_ID,SubDivision_ID,Circle_ID,Sensitivity,Voters_Count,Geofence_Radius,Officer_IDs",
      'BTH001,BTH-CEN-001,Government School Booth 1,14.4421,79.9865,STATION_ID,SUBDIVISION_ID,CIRCLE_ID,NORMAL,850,75,"BADGE001,BADGE002"',
    ].join("\n"),
  },
  SPECIAL_DUTIES: {
    label: "Special Duties",
    icon: "🛡️",
    title: "Specialized Formations",
    description: "Upload Route Mobiles, QRT, Check Posts, FST and other formations.",
    filename: "All_Special_Duties_And_Formations_Template.xlsx",
    template: [
      "Unit_Code,Formation_Wing,Unit_Name,Latitude,Longitude,Base_Police_Station,Vehicle_No,Officer_Name,Officer_Phone,Officer_Designation,Officer_Badge,Shift",
      "UNIT001,ROUTE_MOBILE,Route Mobile 1,12.9716,77.5946,Central Police Station,KA01AB1234,Officer Name,9876543210,POLICE_CONSTABLE,BADGE001,DAY",
    ].join("\n"),
  },
  MASTER: {
    label: "Master Roster",
    icon: "📑",
    title: "Unified District Master Allotment",
    description: "Upload a unified district roster containing multiple duty categories.",
    filename: "Master_Election_Duty_Roster_Template.xlsx",
    template: [
      "Duty_Category,Location_Code,Location_Name,Duty_Wing,Latitude,Longitude,Sensitivity,Police_Station_Name,Allotted_Officer_Name,Officer_Phone,Officer_Designation,Officer_Badge",
      "POLLING_STATION,B001,Government School,POLLING_STATION,12.9716,77.5946,NORMAL,Central Police Station,Officer Name,9876543210,POLICE_CONSTABLE,BADGE001",
    ].join("\n"),
  },
};

const normalizeHeader = (header) =>
  String(header || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "")
    .toLowerCase();

const parseExcelFile = async (file) => {
  const buffer = await file.arrayBuffer();

  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: false,
  });

  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    throw new Error("The Excel file does not contain a worksheet.");
  }

  const worksheet = workbook.Sheets[firstSheetName];

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false,
    blankrows: false,
  });

  if (!rows.length) {
    throw new Error("The selected Excel file is empty.");
  }

  return rows;
};

const getTemplateRows = (template) => {
  const workbook = XLSX.read(template, { type: "string" });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  return XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false,
  });
};

const downloadExcelTemplate = (filename, rows) => {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Roster");
  XLSX.writeFile(workbook, filename);
};
const escapeHTML = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const UploadRoster = ({
  open,
  onClose,
  defaultTab = "BOOTHS",
  tabs = DEFAULT_TABS,
  onUpload,
  onSuccess,
  title = "Upload Roster",
}) => {
  const dispatch = useDispatch();

  const { bulkCreateLoading } = useSelector((state) => state.user);

  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState(defaultTab);
  const config = TAB_CONFIG[activeTab] || TAB_CONFIG.BOOTHS;
  const [excelRows, setExcelRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const headers = excelRows.length ? excelRows[0] : [];

  const dataRows =
    excelRows.length > 1
      ? excelRows.slice(1).filter((row) =>
        row.some((cell) => String(cell ?? "").trim() !== "")
      )
      : [];


  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      setExcelRows([]);
      setFileName("");
      setReplaceExisting(false);
      setError("");
      setSuccessMessage("");
      setUploading(false);
    }
  }, [open, defaultTab]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (event) => {
      if (event.key === "Escape" && !uploading) {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, uploading, onClose]);

  if (!open) return null;

  const resetFile = () => {
    setExcelRows([]);
    setFileName("");
    setError("");
    setSuccessMessage("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTabChange = (tab) => {
    if (uploading) return;

    setActiveTab(tab);
    resetFile();
    setReplaceExisting(false);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();

    if (!["xlsx", "xls"].includes(extension)) {
      setError("Please select an Excel file (.xlsx or .xls).");
      event.target.value = "";
      return;
    }

    try {
      setError("");
      setSuccessMessage("");

      const rows = await parseExcelFile(file);

      if (rows.length < 2) {
        throw new Error(
          "Excel file must contain a header row and at least one data row."
        );
      }

      const headerCount = rows[0].length;

      if (!headerCount || rows[0].every((item) => !String(item).trim())) {
        throw new Error("Excel header row is missing.");
      }

      const invalidRow = rows.slice(1).find(
        (row) => row.length !== headerCount
      );

      if (invalidRow) {
        throw new Error(
          "Some Excel rows have a different number of columns than the header."
        );
      }

      setExcelRows(rows);
      setFileName(file.name);
    } catch (err) {
      setExcelRows([]);
      setFileName("");
      setError(err.message || "Unable to read Excel file.");
      event.target.value = "";
    }
  };

  const downloadTemplate = () => {
    const rows = getTemplateRows(config.template);
    const filename = config.filename.replace(/\\.csv$/i, ".xlsx");
    downloadExcelTemplate(filename, rows);
  };

  const loadSample = () => {
    const rows = getTemplateRows(config.template);
    if (rows.length < 2) {
      setError("No sample template is available for this tab.");
      return;
    }
    setExcelRows(rows);
    setFileName("Sample dataset.xlsx");
    setError("");
    setSuccessMessage("");
  };

  const handleUpload = async () => {
    setError("");
    setSuccessMessage("");

    if (!excelRows.length || !dataRows.length) {
      setError("Please select an Excel file with at least one data row.");
      return;
    }

    if (
      typeof onUpload !== "function" &&
      !["OFFICERS", "SUBDIVISIONS", "CIRCLES", "STATIONS", "BOOTHS"].includes(activeTab)
    ) {
      setError("Upload handler is not configured for this page.");
      return;
    }

    try {
      setUploading(true);

      if (activeTab === "OFFICERS") {
        const officers = mapOfficerRows(headers, dataRows);

        const requiredFields = [
          "username",
          "displayName",
          "phone",
          "password",
          "designation",
        ];

        const invalidRows = officers
          .map((officer, index) => ({
            row: index + 2,
            missing: requiredFields.filter(
              (field) => !officer[field]
            ),
          }))
          .filter((item) => item.missing.length);

        if (invalidRows.length) {
          throw new Error(
            `Required officer fields are missing in Excel row ${invalidRows[0].row}: ${invalidRows[0].missing.join(", ")}`
          );
        }

        // Uses the existing Redux bulkCreateOfficer thunk.
        const result = await dispatch(
          bulkCreateOfficer({
            officers,
            replaceExisting,
          })
        ).unwrap();

        setSuccessMessage(
          result?.message ||
          `${officers.length} officers imported successfully.`
        );
      } else if (activeTab === "SUBDIVISIONS") {
        const subdivisions = mapSubDivisionRows(headers, dataRows);

        const invalidRows = subdivisions
          .map((subdivision, index) => ({
            row: index + 2,
            missing: ["subDivisionId", "name"].filter(
              (field) => !subdivision[field]
            ),
          }))
          .filter((item) => item.missing.length);

        if (invalidRows.length) {
          throw new Error(
            `Required Sub-Division fields are missing in Excel row ${invalidRows[0].row}: ${invalidRows[0].missing.join(", ")}`
          );
        }

        const result = await dispatch(
          bulkCreateSubDivision({ subdivisions })
        ).unwrap();

        const responseData = result?.data || result;
        const createdCount =
          responseData?.totalCreated ??
          responseData?.subdivisions?.length ??
          subdivisions.length;

        setSuccessMessage(
          result?.message ||
          `${createdCount} Sub-Divisions imported successfully.`
        );
      }
      else if (activeTab === "CIRCLES") {
        const circles = mapCircleRows(headers, dataRows);

        const invalidRows = circles
          .map((circle, index) => ({
            row: index + 2,
            missing: ["circleId", "name", "subDivisionId"].filter(
              (field) => !circle[field]
            ),
          }))
          .filter((item) => item.missing.length);

        if (invalidRows.length) {
          throw new Error(
            `Required Circle fields are missing in Excel row ${invalidRows[0].row}: ${invalidRows[0].missing.join(", ")}`
          );
        }

        const result = await dispatch(
          bulkCreateCircle({ circles })
        ).unwrap();

        const responseData = result?.data || result;

        setSuccessMessage(
          result?.message ||
          `${responseData?.successCount ?? circles.length} Circles imported successfully.`
        );
      }
      else if (activeTab === "STATIONS") {
        const stations = mapStationRows(headers, dataRows);

        const invalidRows = stations
          .map((station, index) => ({
            row: index + 2,
            missing: [
              "stationId",
              "code",
              "name",
              "contactNumber",
              "subDivisionId",
              "circleId",
            ].filter((field) => !station[field]),
          }))
          .filter((item) => item.missing.length);

        if (invalidRows.length) {
          throw new Error(
            `Required Station fields are missing in Excel row ${invalidRows[0].row}: ${invalidRows[0].missing.join(", ")}`
          );
        }

        const result = await dispatch(
          bulkCreateStation({ stations })
        ).unwrap();

        const responseData = result?.data || {};

        const successCount = responseData.successCount ?? 0;
        const failedCount = responseData.failedCount ?? 0;

        setSuccessMessage(
          `${successCount} Police Stations imported successfully. ${failedCount ? `${failedCount} records failed.` : ""
          }`
        );

        if (failedCount) {
          console.log("Failed Station Records:", responseData.failed);
        }

        await onSuccess?.();
        setTimeout(() => onClose?.(), 900);

        return;
      }
      else if (activeTab === "BOOTHS") {
        const booths = mapBoothRows(headers, dataRows);

        const invalidRows = booths
          .map((booth, index) => ({
            row: index + 2,
            missing: [
              "boothId",
              "code",
              "name",
              "policeStationId",
              "subDivisionId",
              "circleId",
            ].filter((field) => !booth[field]),
          }))
          .filter((item) => item.missing.length);

        if (invalidRows.length) {
          throw new Error(
            `Required Booth fields are missing in Excel row ${invalidRows[0].row}: ${invalidRows[0].missing.join(", ")}`
          );
        }

        const invalidCoordinates = booths.findIndex(
          (booth) =>
            !Number.isFinite(booth.lat) ||
            booth.lat < -90 ||
            booth.lat > 90 ||
            !Number.isFinite(booth.lng) ||
            booth.lng < -180 ||
            booth.lng > 180
        );

        if (invalidCoordinates !== -1) {
          throw new Error(
            `Invalid latitude or longitude in Excel row ${invalidCoordinates + 2}`
          );
        }

        const result = await dispatch(
          bulkCreateBooth({ booths })
        ).unwrap();

        const responseData = result?.data || {};

        const successCount = responseData.successCount ?? 0;
        const failedCount = responseData.failedCount ?? 0;

        setSuccessMessage(
          `${successCount} Booths imported successfully. ${failedCount ? `${failedCount} records failed.` : ""
          }`
        );

        if (failedCount) {
          console.log("Failed Booth Records:", responseData.failed);
        }
      }
      else {
        const result = await onUpload({

          type: activeTab,

          headers,

          rows: dataRows,

          replaceExisting,

          fileName,

        });

        if (result === false || result?.success === false) {

          throw new Error(result?.message || "Roster upload failed.");

        }

        setSuccessMessage(
          result?.message || "Roster uploaded successfully."
        );
      }

      await onSuccess?.();

      setTimeout(() => {
        onClose?.();
      }, 900);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to upload roster."
      );
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    if (uploading) return;
    onClose?.();
  };

  const mapOfficerRows = (headers, rows) => {
    const normalizedHeaders = headers.map(normalizeHeader);

    return rows.map((row) => {
      const record = {};
      normalizedHeaders.forEach((header, columnIndex) => {
        record[header] = String(row[columnIndex] ?? "").trim();
      });

      return {
        username: record.username || record.user_name,
        displayName: record.name || record.display_name || record.displayname,
        phone: record.phone,
        password: record.password,
        badgeNo: record.badge_no || record.badgeno,
        designation: (record.designation || "POLICE_CONSTABLE").toUpperCase(),

      };
    });
  };

  const mapSubDivisionRows = (headers, rows) => {
    const normalizedHeaders = headers.map(normalizeHeader);

    return rows.map((row) => {
      const record = {};

      normalizedHeaders.forEach((header, columnIndex) => {
        record[header] = String(row[columnIndex] ?? "").trim();
      });


      return {
        subDivisionId:
          record.subdivision_id ||
          record.subdivisionid ||
          record.sub_division_id ||
          record.code ||
          "",
        name: record.name || record.subdivision_name || "",
        phone: record.phone || record.contact_number || "",
        dspOfficerId:
          record.dsp_badge_number ||
          record.dsp_badge_no ||
          record.dsp_badge ||
          record.dspofficerid ||
          record.dsp_officer_id ||
          "",
      };
    });
  };

  const mapCircleRows = (headers, rows) => {
    const normalizedHeaders = headers.map(normalizeHeader);

    return rows.map((row) => {
      const record = {};

      normalizedHeaders.forEach((header, columnIndex) => {
        record[header] = String(row[columnIndex] ?? "").trim();
      });

      return {
        circleId:
          record.circle_id ||
          record.circleid ||
          "",

        name: record.name || record.circle_name || "",

        subDivisionId:
          record.subdivision_id ||
          record.subdivisionid ||
          record.sub_division_id ||
          "",

        inspectorOfficerId:
          record.inspector_badge_number ||
          record.inspector_badge_no ||
          record.inspector_badge ||
          "",

        phone: record.phone || record.contact_number || "",
      };
    });
  };
  const mapStationRows = (headers, rows) => {
    const normalizedHeaders = headers.map(normalizeHeader);

    return rows.map((row) => {
      const record = {};

      normalizedHeaders.forEach((header, columnIndex) => {
        record[header] = String(row[columnIndex] ?? "").trim();
      });

      return {
        stationId:
          record.station_id ||
          record.stationid ||
          "",

        code: record.code || record.station_code || "",

        name: record.name || record.station_name || "",

        subDivisionId:
          record.subdivision_id ||
          record.subdivisionid ||
          record.sub_division_id ||
          "",

        circleId:
          record.circle_id ||
          record.circleid ||
          "",

        shoOfficerId:
          record.sho_badge_number ||
          record.sho_badge_no ||
          record.sho_badge ||
          record.sho_officer_id ||
          "",

        contactNumber:
          record.contact_number ||
          record.phone ||
          "",
      };
    });
  };

  const mapBoothRows = (headers, rows) => {
    const normalizedHeaders = headers.map(normalizeHeader);

    return rows.map((row) => {
      const record = {};

      normalizedHeaders.forEach((header, columnIndex) => {
        record[header] = String(row[columnIndex] ?? "").trim();
      });

      const officerIds = (
        record.officer_ids ||
        record.officerids ||
        record.assigned_officer_ids ||
        ""
      )
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      return {
        boothId:
          record.booth_id ||
          record.boothid ||
          "",

        code:
          record.code ||
          record.booth_code ||
          "",

        name:
          record.name ||
          record.booth_name ||
          "",

        lat: Number(
          record.latitude || record.lat
        ),

        lng: Number(
          record.longitude || record.lng
        ),

        policeStationId:
          record.police_station_id ||
          record.policestation_id ||
          "",

        subDivisionId:
          record.subdivision_id ||
          record.sub_division_id ||
          record.subdivisionid ||
          "",

        circleId:
          record.circle_id ||
          record.circleid ||
          "",

        sectorId:
          record.sector_id ||
          record.sectorid ||
          "",

        sensitivity:
          record.sensitivity || "NORMAL",

        votersCount: Number(
          record.voters_count || 0
        ),

        geofenceRadius: Number(
          record.geofence_radius || 75
        ),

        assignedStaff: officerIds.map((officerId) => ({
          officerId,
          dutyWing: "POLLING_STATION",
          generalNumber: null,
        })),
      };
    });
  };
  return (
    <div
      className="ur-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="ur-modal">
        {/* Header */}
        <div className="ur-header">
          <div className="ur-header-left">
            <div className="ur-header-icon">📤</div>

            <div>
              <h2>{title}</h2>
              <p>Import and manage election duty records</p>
            </div>
          </div>

          <button
            type="button"
            className="ur-close-btn"
            onClick={closeModal}
            disabled={uploading}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="ur-content">
          {/* Category tabs */}
          {tabs.length > 1 && (
            <div className="ur-tabs">
              {tabs.map((tab) => {
                const item = TAB_CONFIG[tab];

                if (!item) return null;

                return (
                  <button
                    type="button"
                    key={tab}
                    className={`ur-tab ${activeTab === tab ? "active" : ""
                      }`}
                    onClick={() => handleTabChange(tab)}
                    disabled={uploading}
                  >
                    <span>{item.icon}</span>
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Upload information */}
          <div className="ur-guidance">
            <div className="ur-guidance-icon">
              {config.icon}
            </div>

            <div className="ur-guidance-text">
              <h3>{config.title}</h3>
              <p>{config.description}</p>
            </div>

            <button
              type="button"
              className="ur-template-btn"
              onClick={downloadTemplate}
            >
              📥 Download Template
            </button>
          </div>

          {/* File upload */}
          <div className="ur-section">
            <label className="ur-label">
              Select Excel File
            </label>

            <div
              className="ur-dropzone"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
                hidden
              />

              <div className="ur-upload-icon">📊</div>

              <h4>Click to select your Excel file</h4>

              <p>Excel files only (.xlsx, .xls)</p>

              {fileName && (
                <div className="ur-file-name">
                  <span>📄</span>
                  {fileName}
                </div>
              )}
            </div>
          </div>

          <div className="ur-file-actions">
            <button
              type="button"
              className="ur-text-btn"
              onClick={loadSample}
            >
              Load Sample Data
            </button>

            {excelRows.length > 0 && (
              <button
                type="button"
                className="ur-text-btn ur-remove-btn"
                onClick={resetFile}
              >
                Remove File
              </button>
            )}
          </div>
        </div>

        {/* Excel preview */}
        {excelRows.length > 0 && (
          <div className="ur-preview">
            <div className="ur-preview-header">
              <div>
                <h3>Excel Preview</h3>
                <p>
                  {dataRows.length} records found
                </p>
              </div>

              <span className="ur-count">
                {dataRows.length} Rows
              </span>
            </div>

            {excelRows.length < 2 ? (
              <div className="ur-empty">
                No data rows found in this Excel file.
              </div>
            ) : (
              <div className="ur-table-wrapper">
                <table className="ur-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      {headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {dataRows.slice(0, 10).map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td>{rowIndex + 1}</td>

                        {headers.map((_, colIndex) => (
                          <td key={colIndex}>
                            {escapeHTML(row[colIndex] ?? "--")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {dataRows.length > 10 && (
                  <div className="ur-more-rows">
                    Showing first 10 of {dataRows.length} records.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Replace option */}
        <div className="ur-replace-box">
          <label>
            <input
              type="checkbox"
              checked={replaceExisting}
              onChange={(event) =>
                setReplaceExisting(event.target.checked)
              }
              disabled={uploading}
            />

            <span>
              <strong>Replace existing records</strong>
              <small>
                Enable only when the selected import API supports a fresh overwrite.
              </small>
            </span>
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="ur-message ur-error">
            <span>⚠️</span>
            {error}
          </div>
        )}

        {/* Success */}
        {successMessage && (
          <div className="ur-message ur-success">
            <span>✓</span>
            {successMessage}
          </div>
        )}

        {/* Footer */}
        <div className="ur-footer">
          <div className="ur-footer-info">
            {excelRows.length
              ? `${dataRows.length} records ready`
              : "Select an Excel file to continue"}
          </div>

          <div className="ur-footer-actions">
            <button
              type="button"
              className="ur-cancel-btn"
              onClick={closeModal}
              disabled={uploading}
            >
              Cancel
            </button>

            <button
              type="button"
              className="ur-upload-btn"
              onClick={handleUpload}
              disabled={uploading || !dataRows.length}
            >
              {uploading ? (
                <>
                  <span className="ur-spinner" />
                  Uploading...
                </>
              ) : (
                <>
                  🚀 Upload & Process
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadRoster;
